import {
  Home,
  Users,
  Link,
  Sparkles,
  ChevronDown,
  Bot,
  Building2,
  CoinsIcon,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface SubMenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  badge?: string;
  hidden?: boolean;
}

interface MenuItem {
  label: string;
  path?: string;
  icon: React.ReactNode;
  subItems?: SubMenuItem[];
}

const ExpandableMenuItem = ({
  item,
  isExpanded,
  onToggle,
}: {
  item: MenuItem;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  if (!item.subItems) {
    return (
      <NavLink
        to={item.path!}
        end={item.path === "/dashboard"}
        className={({ isActive }) =>
          cn(
            "w-full flex items-center px-4 py-2 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors",
            isActive && "bg-green-50 text-green-700"
          )
        }
      >
        {item.icon}
        <span>{item.label}</span>
      </NavLink>
    );
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={onToggle}
        className={cn(
          "w-full flex items-center justify-between px-4 py-2 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors",
          isExpanded && "bg-green-50 text-green-700"
        )}
      >
        <div className="flex items-center">
          {item.icon}
          <span>{item.label}</span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform",
            isExpanded && "transform rotate-180"
          )}
        />
      </button>
      {isExpanded && (
        <div className="mt-1 space-y-1">
          {item.subItems.map(
            (subItem, index) =>
              !subItem.hidden && (
                <NavLink
                  key={index}
                  to={subItem.path}
                  className={({ isActive }) =>
                    cn(
                      "w-full flex items-center px-8 py-2 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors",
                      isActive && "bg-green-50 text-green-700"
                    )
                  }
                >
                  {subItem.icon}
                  <span>{subItem.label}</span>
                  {subItem.badge && (
                    <span className="ml-2 text-xs text-red-500">
                      {subItem.badge}
                    </span>
                  )}
                </NavLink>
              )
          )}
        </div>
      )}
    </div>
  );
};

export const NPOSidebar = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const sidebarItems: MenuItem[] = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <Home className="mr-2 h-4 w-4" />,
    },
    {
      label: "Community Events",
      icon: <Users className="mr-2 h-4 w-4" />,
      path: "/dashboard/community-events",
    },
    {
      label: "Corporate Events",
      icon: <Building2 className="mr-2 h-4 w-4" />,
      path: "/dashboard/corporate-events",
    },
    {
      label: "Rate Cards",
      icon: <CoinsIcon className="mr-2 h-4 w-4" />,
      path: "/dashboard/rate-cards",
    },
    {
      label: "Marketing & Content",
      icon: <Sparkles className="mr-2 h-4 w-4" />,
      path: "/dashboard/ai-tools",
    },
    {
      label: "Coraa AI Co-Pilot",
      icon: <Bot className="mr-2 h-4 w-4" />,
      path: "/dashboard/chatbot",
    },
    {
      label: "My Microsite",
      icon: <Link className="mr-2 h-4 w-4" />,
      path: "/dashboard/mission-hub",
    },
  ];

  return (
    <div className="pb-12 w-[280px]">
      <div className="space-y-4 py-4">
        <div className="px-3 py-2">
          <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
            My CauseConnect
          </h2>
          <div className="space-y-1">
            {sidebarItems.map((item, index) => (
              <ExpandableMenuItem
                key={index}
                item={item}
                isExpanded={expandedIndex === index}
                onToggle={() => {
                  if (expandedIndex === index) {
                    setExpandedIndex(null);
                  } else {
                    setExpandedIndex(index);
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
